// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCreateOperatorConfiguration } from '@hooks/mutations/collateral/useCreateOperatorConfiguration';
import { useCallback, useState } from 'react';
import { Box } from '@mui/material';
import FormDialog from '../../../components/Dialog/FormDialog';
import { useModuleParty } from '../../../contexts/ModuleContext';
import {
  PartyCredentialRequirementElement,
  toPartyCredentialRequirements,
} from '../../../modules/Credential/Common';
import RequiredCredentialsInput from '../../../modules/Credential/RequiredCredentialsInput';

type DialogProps = {
  open: boolean;
  onClose: () => void;
};

export const CreateOperatorConfigurationDialog = ({ open, onClose }: DialogProps) => {
  const [providerCredentialRequirements, setProviderCredentialRequirements] = useState<
    PartyCredentialRequirementElement[]
  >([]);
  const party = useModuleParty();
  const createOperatorConfiguration = useCreateOperatorConfiguration();

  const resetState = useCallback(() => {
    setProviderCredentialRequirements([]);
  }, [setProviderCredentialRequirements]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleSubmit = useCallback(async () => {
    await createOperatorConfiguration({
      argument: {
        operator: party,
        userRequirements: toPartyCredentialRequirements(providerCredentialRequirements),
      },
    });
    handleClose();
  }, [party, providerCredentialRequirements, createOperatorConfiguration, handleClose]);

  return (
    <FormDialog
      open={open}
      title="Create Operator Configuration"
      onCancel={handleClose}
      onSubmit={handleSubmit}
      submitName="Create"
      isSubmitDisabled={false}
    >
      <Box mb={3}>
        <RequiredCredentialsInput
          list={providerCredentialRequirements}
          setList={setProviderCredentialRequirements}
          title="Required Credentials for Users"
        />
      </Box>
    </FormDialog>
  );
};
